import { Link } from 'react-router-dom';
import { LogIn, GraduationCap, Brain, Trophy, Sparkles, ArrowRight } from 'lucide-react';
import { TornCard } from '@branon-shared/ui/TornCard';
import { IconBadge } from '@branon-shared/ui/IconBadge';
import { AmbientDoodles, SpillingDoodles } from '@branon-shared/ui/Doodle';

function Feature({ icon: Icon, label, tone }: { icon: typeof Brain; label: string; tone: 'coral' | 'marigold' }) {
  return (
    <div className="flex flex-col items-center gap-2 w-24">
      <IconBadge icon={Icon} tone={tone} size={48} rotate={tone === 'marigold' ? -4 : 4} />
      <span className="font-quicksand text-xs font-bold text-white/90 text-center leading-tight">{label}</span>
    </div>
  );
}

export default function Welcome() {
  return (
    <div className="min-h-screen bg-night relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 pointer-events-none">
        <AmbientDoodles />
      </div>

      <div className="relative z-10 p-4 sm:p-6">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Branon Logo" className="h-10 w-10" />
          <img src="/wordmark-white.png" alt="Branon" className="h-7 hidden sm:block" />
        </div>
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center px-4 pb-12">
        <div className="max-w-2xl w-full">
          <TornCard
            spill={<SpillingDoodles />}
            stage={
              <div className="text-center pb-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 text-marigold text-xs font-bold uppercase tracking-widest mb-5">
                  <Sparkles className="w-3 h-3" />
                  Exam Prep Platform
                </div>
                <h1 className="font-quicksand text-4xl sm:text-5xl font-bold text-white mb-3 leading-tight text-balance">
                  Ready to actually <span className="text-marigold">get it</span>?
                </h1>
                <p className="text-white/70 max-w-md mx-auto mb-8 font-inter">
                  Master concepts and prepare for exams through focused practice, flashcards, and real test conditions.
                </p>
                <div className="flex items-center justify-center gap-6 sm:gap-10">
                  <Feature icon={Brain} label="Smart learning" tone="coral" />
                  <Feature icon={GraduationCap} label="Exam prep" tone="marigold" />
                  <Feature icon={Trophy} label="Track progress" tone="coral" />
                </div>
              </div>
            }
          >
            <div className="text-center">
              <IconBadge icon={LogIn} tone="coral" size={56} rotate={-4} style={{ margin: '0 auto 1rem' }} />
              <h2 className="font-quicksand text-2xl font-bold text-ink mb-1">Student Login</h2>
              <p className="text-gray-600 mb-6 font-inter">
                Use your student code and MPIN from your school administrator.
              </p>
              <div className="inline-block text-left bg-gray-50 border-2 border-gray-200 rounded-card px-4 py-3 mb-7">
                <p className="text-xs text-gray-600 font-semibold mb-1">Student code format</p>
                <p className="font-mono text-lg text-ink font-bold tracking-wide">STU + 6 characters</p>
              </div>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 bg-coral text-white font-quicksand font-bold text-base px-7 py-3.5 rounded-btn border-2 border-ink shadow-sticker hover:translate-y-0.5 hover:shadow-none transition-all"
              >
                Continue to login
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </TornCard>

          <p className="text-center text-white/50 mt-6 text-sm font-inter">
            Don't have login credentials? Contact your school administrator to get started
          </p>
        </div>
      </div>
    </div>
  );
}
